// frontend/src/RequestForm.js
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { countries } from './countries';
import './RequestForm.css';

// -----------------------------------------
// Constants
// -----------------------------------------
const INITIAL_FORM = {
  serviceType: '',
  projectName: '',
  companyName: '',
  contactName: '',
  email: '',
  countryCode: '',
  phone: '',
  budget: '',
  timeline: '',
  description: '',
  requirements: '',
};

// Fixed budget mapping to match form values
const BUDGET_MAPPING = {
  "under-5k": 5000,
  "5k-15k": 15000,
  "15k-30k": 30000,
  "30k-50k": 50000,
  "over-50k": 50000,
  "not-sure": null,
};

// Validation patterns
const VALIDATION = {
  email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  phone: /^[0-9+()\-\s]{10,20}$/,
};

// -----------------------------------------
// Component
// -----------------------------------------
const RequestForm = () => {
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [status, setStatus] = useState('');
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [errors, setErrors] = useState({});

  const countryDropdownRef = useRef(null);
  const countryListId = "country-list";

  // -----------------------------------------
  // Filter Countries (Memoized)
  // -----------------------------------------
  const filteredCountries = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return countries.filter(
      (country) =>
        country.name.toLowerCase().includes(term) ||
        country.code.includes(term) ||
        country.short.toLowerCase().includes(term)
    );
  }, [searchTerm]);

  // -----------------------------------------
  // Close dropdown on outside click & escape key
  // -----------------------------------------
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        countryDropdownRef.current &&
        !countryDropdownRef.current.contains(event.target)
      ) {
        setShowCountryDropdown(false);
      }
    };

    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        setShowCountryDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscapeKey);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, []);

  // -----------------------------------------
  // Form Validation
  // -----------------------------------------
  const validateForm = () => {
    const newErrors = {};

    // Required fields
    if (!formData.serviceType) newErrors.serviceType = 'Service type is required';
    if (!formData.contactName.trim()) newErrors.contactName = 'Contact name is required';
    
    // Email validation
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!VALIDATION.email.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    // Phone validation
    if (!formData.phone) {
      newErrors.phone = 'Phone number is required';
    } else if (!VALIDATION.phone.test(formData.phone.replace(/\s/g, ''))) {
      newErrors.phone = 'Please enter a valid phone number';
    }
    
    if (!formData.countryCode) newErrors.countryCode = 'Country code is required';
    if (!formData.budget) newErrors.budget = 'Budget range is required';
    if (!formData.description.trim()) newErrors.description = 'Project description is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // -----------------------------------------
  // Handlers
  // -----------------------------------------
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleCountrySelect = (countryCode) => {
    setFormData((prev) => ({ ...prev, countryCode }));
    setShowCountryDropdown(false);
    setSearchTerm('');
    
    // Clear country code error
    if (errors.countryCode) {
      setErrors((prev) => ({ ...prev, countryCode: '' }));
    }
  };

  const resetForm = () => {
    setFormData(INITIAL_FORM);
    setStatus('');
    setErrors({});
  };

  // -----------------------------------------
  // Submit Handler
  // -----------------------------------------
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form
    if (!validateForm()) {
      setStatus('Please fix the errors in the form.');
      return;
    }

    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
    if (!API_BASE_URL) {
      setStatus("Error: API base URL not configured.");
      return;
    }

    setStatus('Submitting...');

    const payload = {
      name: formData.contactName.trim(),
      email: formData.email.trim(),
      service_type: formData.serviceType,
      project_name: formData.projectName.trim(),
      institution_name: formData.companyName.trim(),
      contact_phone: formData.phone,
      country_code: formData.countryCode,
      timeline: formData.timeline,
      your_budget: BUDGET_MAPPING[formData.budget],
      description: formData.description.trim(),
      requirements: formData.requirements.trim(),
    };

    try {
      const response = await fetch(`${API_BASE_URL}/service-request/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await response.json().catch(() => null);

      if (response.ok) {
        setStatus('Success! We received your request.');
        resetForm();
        
        // Optional: Reset after 5 seconds
        setTimeout(() => {
          setStatus('');
        }, 5000);
      } else {
        console.error("Server error:", data);
        const errorMessage = data?.detail || data?.message || 'Failed to submit. Please try again.';
        setStatus(`Error: ${errorMessage}`);
      }
    } catch (error) {
      console.error(error);
      setStatus('Error connecting to server. Please check your connection.');
    }
  };

  // -----------------------------------------
  // Selected Country
  // -----------------------------------------
  const selectedCountry = countries.find(
    (c) => c.code === formData.countryCode
  );

  // -----------------------------------------
  // JSX
  // -----------------------------------------
  return (
    <div className="container request-form-page">
      <div className="header">
        <h1>Request New Service</h1>
        <p>Fill out the form below to submit a service request</p>
      </div>

      <div className="form-container">
        <form onSubmit={handleSubmit} noValidate>

          {/* -----------------------------------------
              Service Details Section
          ----------------------------------------- */}
          <div className="form-section">
            <h2 className="section-title">
              <i className="fas fa-clipboard-list" aria-hidden="true"></i> Service Details
            </h2>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="serviceType" className="required">
                  Service Type
                </label>
                <select
                  id="serviceType"
                  name="serviceType"
                  value={formData.serviceType}
                  onChange={handleChange}
                  required
                  className={errors.serviceType ? 'error' : ''}
                  aria-describedby={errors.serviceType ? 'serviceType-error' : undefined}
                >
                  <option value="">Select a service</option>
                  <option value="web-development">Web Development</option>
                  <option value="mobile-app">Mobile App Development</option>
                  <option value="ui-ux">UI/UX Design</option>
                  <option value="consulting">IT Consulting</option>
                  <option value="digital-marketing">Digital Marketing</option>
                  <option value="data-analysis">Data Analysis</option>
                  <option value="payment-integration">Payment Integration</option>
                  <option value="web-hosting">Web Hosting</option>
                  <option value="other">Other</option>
                </select>
                {errors.serviceType && (
                  <span id="serviceType-error" className="error-message">
                    {errors.serviceType}
                  </span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="projectName" className="optional">Project Name</label>
                <input
                  type="text"
                  id="projectName"
                  name="projectName"
                  value={formData.projectName}
                  onChange={handleChange}
                  placeholder="Enter project name if applicable"
                />
                <div className="form-note">If you already have a project name in mind</div>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="companyName" className="optional">Company Name</label>
                <input
                  type="text"
                  id="companyName"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleChange}
                  placeholder="Your company name"
                />
              </div>
            </div>
          </div>

          {/* -----------------------------------------
              Contact Information
          ----------------------------------------- */}
          <div className="form-section">
            <h2 className="section-title">
              <i className="fas fa-user-circle" aria-hidden="true"></i> Contact Information
            </h2>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="contactName" className="required">Contact Name</label>
                <input
                  type="text"
                  id="contactName"
                  name="contactName"
                  value={formData.contactName}
                  onChange={handleChange}
                  placeholder="Full name"
                  required
                  className={errors.contactName ? 'error' : ''}
                  aria-describedby={errors.contactName ? 'contactName-error' : undefined}
                />
                {errors.contactName && (
                  <span id="contactName-error" className="error-message">
                    {errors.contactName}
                  </span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="email" className="required">Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="you@example.com"
                  required
                  className={errors.email ? 'error' : ''}
                  aria-describedby={errors.email ? 'email-error' : undefined}
                />
                {errors.email && (
                  <span id="email-error" className="error-message">
                    {errors.email}
                  </span>
                )}
              </div>
            </div>

            {/* Phone Field with Label */}
            <div className="form-group" style={{ flex: '1 0 100%' }}>
              <label htmlFor="phone" className="required">Phone Number</label>
              <div className="phone-group" ref={countryDropdownRef}>
                <div className="country-selector">
                  <div 
                    className={`selected-country ${errors.countryCode ? 'error' : ''}`}
                    onClick={() => setShowCountryDropdown(prev => !prev)}
                    role="combobox"
                    aria-expanded={showCountryDropdown}
                    aria-controls={countryListId}
                    aria-label="Select country code"
                    tabIndex="0"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setShowCountryDropdown(prev => !prev);
                      }
                    }}
                  >
                    <span className="country-flag">
                      {selectedCountry ? selectedCountry.flag : "🌐"}
                    </span>
                    <span className="country-code">
                      {selectedCountry ? selectedCountry.code : "Select"}
                    </span>
                    
                    <i 
                      className={`fas ${showCountryDropdown ? 'fa-chevron-up' : 'fa-chevron-down'}`}
                      style={{ 
                        marginLeft: 'auto', 
                        fontSize: '12px',
                        color: errors.countryCode ? '#dc3545' : '#666',
                        transition: 'transform 0.3s ease'
                      }}
                      aria-hidden="true"
                    ></i>
                  </div>

                  {showCountryDropdown && (
                    <div 
                      className="country-list visible"
                      id={countryListId}
                      role="listbox"
                    >
                      <div className="country-search">
                        <i className="fas fa-search" style={{
                          position: 'absolute',
                          left: '25px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          color: '#999'
                        }} aria-hidden="true"></i>
                        <input
                          type="text"
                          placeholder="Search countries..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          autoFocus
                          style={{ paddingLeft: '35px' }}
                          aria-label="Search countries"
                        />
                      </div>
                      
                      {filteredCountries.length > 0 ? (
                        filteredCountries.map((country) => (
                          <div
                            key={country.code}
                            className={`country-option ${
                              formData.countryCode === country.code ? 'selected' : ''
                            }`}
                            onClick={() => handleCountrySelect(country.code)}
                            role="option"
                            aria-selected={formData.countryCode === country.code}
                            tabIndex="0"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                handleCountrySelect(country.code);
                              }
                            }}
                          >
                            <span className="country-flag" aria-hidden="true">{country.flag}</span>
                            <span className="country-name">{country.name}</span>
                            <span className="country-code-text">{country.code}</span>
                          </div>
                        ))
                      ) : (
                        <div className="no-results">No countries found</div>
                      )}
                    </div>
                  )}
                </div>

                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="Phone number"
                  required
                  className={errors.phone ? 'error' : ''}
                  aria-describedby={errors.phone ? 'phone-error' : undefined}
                />
              </div>
              {errors.countryCode && (
                <span id="countryCode-error" className="error-message">
                  {errors.countryCode}
                </span>
              )}
              {errors.phone && (
                <span id="phone-error" className="error-message">
                  {errors.phone}
                </span>
              )}
            </div>
          </div>

          {/* -----------------------------------------
              Project Details
          ----------------------------------------- */}
          <div className="form-section">
            <h2 className="section-title">
              <i className="fas fa-project-diagram" aria-hidden="true"></i> Project Details
            </h2>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="budget" className="required">Budget Range</label>
                <select
                  id="budget"
                  name="budget"
                  value={formData.budget}
                  onChange={handleChange}
                  required
                  className={errors.budget ? 'error' : ''}
                  aria-describedby={errors.budget ? 'budget-error' : undefined}
                >
                  <option value="">Select budget</option>
                  <option value="under-5k">Under $5,000</option>
                  <option value="5k-15k">$5,000 - $15,000</option>
                  <option value="15k-30k">$15,000 - $30,000</option>
                  <option value="30k-50k">$30,000 - $50,000</option>
                  <option value="over-50k">Over $50,000</option>
                  <option value="not-sure">Not sure / Need quote</option>
                </select>
                {errors.budget && (
                  <span id="budget-error" className="error-message">
                    {errors.budget}
                  </span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="timeline" className="optional">Timeline</label>
                <select
                  id="timeline"
                  name="timeline"
                  value={formData.timeline}
                  onChange={handleChange}
                >
                  <option value="">Select timeline (Optional)</option>
                  <option value="asap">As soon as possible</option>
                  <option value="1-3months">1-3 months</option>
                  <option value="3-6months">3-6 months</option>
                  <option value="6plus">6+ months</option>
                  <option value="flexible">Flexible</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="description" className="required">
                  Project Description
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Please describe your project, goals, and any specific needs..."
                  required
                  className={errors.description ? 'error' : ''}
                  aria-describedby={errors.description ? 'description-error' : undefined}
                  rows="4"
                />
                <div className="form-note">
                  Be as detailed as possible to help us understand your requirements
                </div>
                {errors.description && (
                  <span id="description-error" className="error-message">
                    {errors.description}
                  </span>
                )}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="requirements" className="optional">
                  Specific Requirements
                </label>
                <textarea
                  id="requirements"
                  name="requirements"
                  value={formData.requirements}
                  onChange={handleChange}
                  placeholder="Any specific technologies, platforms, or other requirements..."
                  rows="3"
                />
                <div className="form-note">
                  Any specific constraints or technologies to follow
                </div>
              </div>
            </div>
          </div>

          {/* -----------------------------------------
              Buttons
          ----------------------------------------- */}
          <div className="button-container">
            <button
              type="button"
              className="btn btn-cancel"
              onClick={() => {
                if (window.confirm('Are you sure you want to cancel? All entered data will be lost.')) {
                  resetForm();
                }
              }}
            >
              Cancel
            </button>

            <button type="submit" className="btn btn-submit">
              Submit Request
            </button>
          </div>

          {status && (
            <p 
              className={`form-status ${status.includes('Success') ? 'success' : 'error'}`}
              role="alert"
            >
              {status}
            </p>
          )}
        </form>
      </div>

      <div className="footer">© 2025 Moonlanding Technologies Ltd. All rights reserved.</div>
    </div>
  );
};

export default RequestForm;